import { Region } from '@/common/enums/region.enum'

export interface IRuntimeConfig {
  BASE_URL: string
  FRONTEND_URL: string
  REGION: string
  CURRENCY: string
  IS_PRODUCTION: boolean
  APPINSIGHTS_CONNECTION_STRING: string
  // Auth (antes build-time NEXT_PUBLIC_*, ahora runtime para build agnóstico → cascade)
  CLIENT_ID: string
  API_SCOPE: string
}

const DEFAULT_CONFIG: IRuntimeConfig = {
  BASE_URL: '',
  FRONTEND_URL: '',
  REGION: Region.Ecuador,
  CURRENCY: '',
  IS_PRODUCTION: false,
  APPINSIGHTS_CONNECTION_STRING: '',
  CLIENT_ID: '',
  API_SCOPE: '',
}

let hydratedConfig: IRuntimeConfig | null = null

// Lee process.env directamente (server-side).
// En server las Application Settings de App Service se exponen como process.env.
// Compatibilidad con dev local: si APP_* no existe, cae a NEXT_PUBLIC_APP_*.
const readFromServerEnv = (): IRuntimeConfig => ({
  BASE_URL: process.env.APP_BACKEND ?? process.env.NEXT_PUBLIC_APP_BACKEND ?? '',
  FRONTEND_URL:
    process.env.APP_URL_FRONTEND ?? process.env.NEXT_PUBLIC_APP_URL_FRONTEND ?? '',
  REGION: process.env.APP_REGION ?? process.env.NEXT_PUBLIC_APP_REGION ?? Region.Ecuador,
  CURRENCY: process.env.APP_CURRENCY ?? process.env.NEXT_PUBLIC_APP_CURRENCY ?? '',
  IS_PRODUCTION:
    (process.env.APP_PRODUCTION ?? process.env.NEXT_PUBLIC_APP_PRODUCTION) === 'true',
  // El tercer fallback es el nombre que usa la instrumentacion automatica de App Service, y es el
  // que de hecho esta configurado en los App Service del frontend: `APP_APPINSIGHTS_CONNECTION_STRING`
  // no esta definida en NINGUNO de los cuatro (EC UAT, EC prod, PE UAT, PE prod), asi que todos
  // caian al recurso legado hardcodeado en `AppInsights.ts` —que es el de QA— y la telemetria de los
  // cuatro ambientes terminaba mezclada en un solo recurso llamado MTPerseveranceQA.
  APPINSIGHTS_CONNECTION_STRING:
    process.env.APP_APPINSIGHTS_CONNECTION_STRING ??
    process.env.NEXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING ??
    process.env.APPLICATIONINSIGHTS_CONNECTION_STRING ??
    '',
  CLIENT_ID: process.env.APP_CLIENT_ID ?? process.env.NEXT_PUBLIC_APP_CLIENT_ID ?? '',
  API_SCOPE:
    process.env.APP_SCOPE_XTS_ID_BACKEND ??
    process.env.NEXT_PUBLIC_APP_SCOPE_XTS_ID_BACKEND ??
    '',
})

// Getter isomorphic:
// - Server-side (route handlers, middleware, RSC): lee process.env cada vez.
// - Client-side: lee el singleton hidratado por ConfigRuntimeProvider.
// - Client antes de hidratar: retorna DEFAULT_CONFIG (el provider muestra loader
//   hasta hidratar, así que este caso normalmente no impacta a código de usuario).
export const getRuntimeConfig = (): IRuntimeConfig => {
  if (typeof window === 'undefined') {
    return readFromServerEnv()
  }

  return hydratedConfig ?? DEFAULT_CONFIG
}

export const setRuntimeConfig = (config: IRuntimeConfig): void => {
  hydratedConfig = config
}

export const isRuntimeConfigHydrated = (): boolean => {
  return hydratedConfig !== null
}
